import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { queryClient } from '.';
import { type Station, type EditStation, type StationSort } from '../../shared/types';
import { deleteAdminStation, getAdminStations, postAdminStation, putAdminStation, postAdminStationsSort } from '../utilities/api';

export const queryKeys = {
    getAdminStations: () => ['admin', 'stations', 'get'],
};

export const useAdminStations = (): UseQueryResult<Station[], Error> => {
    return useQuery({ queryKey: queryKeys.getAdminStations(), queryFn: getAdminStations, staleTime: Infinity });
};

export const useUpdateAdminStation = () => {
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: EditStation }) => putAdminStation(id, data),
        onSuccess: (newStation: Station) => {
            queryClient.setQueryData(queryKeys.getAdminStations(), (stations: Station[]) => {
                return stations.map((station) => (station._id === newStation._id ? newStation : station));
            });
        },
    });
};

export const useCreateAdminStation = () => {
    return useMutation({
        mutationFn: (data: EditStation) => postAdminStation(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.getAdminStations() }),
    });
};

export const useDeleteAdminStation = () => {
    return useMutation({
        mutationFn: (id: string) => deleteAdminStation(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.getAdminStations() });
        },
    });
};

export const useAdminStationSort = () => {
    return useMutation({
        mutationFn: (data: StationSort) => postAdminStationsSort(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.getAdminStations() });
        },
    });
};
